import { Box, Grid } from "@mui/material";
import type { BoxProps, GridProps } from "@mui/material";
import { get, isNumber } from "lodash";
import { Children, useMemo, cloneElement } from "react";
import type { ReactElement, ReactNode } from "react";
import { v4 } from "uuid";
import { Formik, Form as FormBody } from "formik";
import type { FormikConfig, FormikValues } from "formik";

import { FormContext } from "@/context";
import { Select } from "./form-fields/select";
import { TextField } from "./form-fields/text-field";
import { CodeVerification } from "./form-fields/code-verification";
import { DatePicker } from "./form-fields/date-picker";
import { Label } from "./form-fields/label";
import { Switch } from "./form-fields/switch";
import { Checkbox } from "./form-fields/checkbox";
import { AutoComplete } from "./form-fields/auto-complete";
import { PhoneNumber } from "./form-fields/text-field/phone-number";
import { Radio } from "./form-fields/radio";

type ResponsiveColumns = {
  xs?: number;
  md?: number;
  lg?: number;
};

type FormLayout = {
  type: "vertical" | "horizontal";
  maxColumns: number | ResponsiveColumns;
};

type FormChildFlex = number | ResponsiveColumns;

interface FormChildProps {
  type?: string;
  flex?: FormChildFlex;
  loading?: boolean;
}

export interface FormProps {
  initialValues?: FormikValues;
  validationSchema?: unknown;
  validateOnMount?: boolean;
  validateOnChange?: boolean;
  onSubmit?: FormikConfig<FormikValues>["onSubmit"];
  children?: ReactNode;
  loading?: boolean;
  layout?: FormLayout;
  sx?: BoxProps["sx"];
  spacing?: GridProps["spacing"];
}

function FormBase({
  initialValues,
  validationSchema,
  validateOnMount,
  validateOnChange,
  onSubmit,
  children,
  loading,
  layout,
  sx,
  spacing,
}: FormProps) {
  const context = useMemo(() => ({ loading }), [loading]);

  const getLayoutDefaults = (key: keyof ResponsiveColumns) => {
    if (get(layout?.maxColumns, key)) {
      return 12 / Number(get(layout?.maxColumns, key));
    }

    if (isNumber(layout?.maxColumns)) {
      return 12 / layout.maxColumns;
    }

    switch (key) {
      case "xs":
        return 12;
      case "md":
        return 6;
      case "lg":
        return 4;
      default:
        return 12;
    }
  };

  const calculateColumnFlex = (
    flex: FormChildFlex | undefined,
    key: keyof ResponsiveColumns
  ): number => {
    if (!flex) {
      return getLayoutDefaults(key);
    }

    if (isNumber(flex)) {
      return flex;
    }

    if (get(flex, key)) {
      return Number(get(flex, key));
    }

    if (key === "lg") {
      return calculateColumnFlex(flex, "md");
    }

    if (key === "md") {
      return calculateColumnFlex(flex, "xs");
    }

    throw new Error("Property for xs or a wider one should be provided");
  };

  return (
    <FormContext.Provider value={context}>
      <Formik
        initialValues={initialValues as FormikValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit as FormikConfig<FormikValues>["onSubmit"]}
        enableReinitialize
        validateOnMount={validateOnMount}
        validateOnChange={validateOnChange}
      >
        <FormBody>
          <Box sx={sx}>
            {!layout ? (
              children
            ) : (
              <Grid
                container
                spacing={spacing ?? 4}
                wrap="wrap"
                justifyContent="space-between"
                alignItems="stretch"
              >
                {Children.map(Children.toArray(children), (child) => {
                  const childElement = child as ReactElement<FormChildProps>;

                  return childElement.props.type === "submit" ? (
                    <Grid item key={v4()} xs={12}>
                      {cloneElement(childElement, {
                        loading: loading ?? context?.loading,
                      })}
                    </Grid>
                  ) : (
                    <Grid
                      item
                      key={v4()}
                      xs={calculateColumnFlex(childElement.props.flex, "xs")}
                      md={calculateColumnFlex(childElement.props.flex, "md")}
                      lg={calculateColumnFlex(childElement.props.flex, "lg")}
                    >
                      {childElement}
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </FormBody>
      </Formik>
    </FormContext.Provider>
  );
}

type FormComponent = typeof FormBase & {
  Select: typeof Select;
  TextField: typeof TextField;
  CodeVerification: typeof CodeVerification;
  PhoneNumber: typeof PhoneNumber;
  DatePicker: typeof DatePicker;
  Label: typeof Label;
  Switch: typeof Switch;
  Checkbox: typeof Checkbox;
  AutoComplete: typeof AutoComplete;
  Radio: typeof Radio;
};

export const Form = FormBase as FormComponent;
Form.Select = Select;
Form.TextField = TextField;
Form.CodeVerification = CodeVerification;
Form.PhoneNumber = PhoneNumber;
Form.DatePicker = DatePicker;
Form.Label = Label;
Form.Switch = Switch;
Form.Checkbox = Checkbox;
Form.AutoComplete = AutoComplete;
Form.Radio = Radio;
